/**
 * /ai/feedback — per-tenant chat-feedback history.
 *
 * Every 👍 / 👎 the team has clicked on a chat answer, newest
 * first. HR uses this to:
 *   - Spot AI answers that confused people (👎 + comment).
 *   - Audit which kinds of questions tend to fail.
 *   - Hand specific failures to Solvait support without
 *     having to reconstruct the conversation.
 *
 * Visibility is tenant-wide: anyone with access to the AI module
 * sees every feedback row in the tenant. Only the user who left
 * a row can clear it (server-side ownership check).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  User,
} from "lucide-react";
import { useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { EmptyState, PageHeader } from "../components/ui";
import { api } from "../lib/api";
import type { ChatFeedback } from "../lib/api";

type SentimentFilter = "all" | "up" | "down";

export function AIFeedbackPage() {
  const { t } = useTranslation();
  const { user: clerkUser } = useUser();
  const me = clerkUser?.id ?? null;
  const [sentimentFilter, setSentimentFilter] =
    useState<SentimentFilter>("all");
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["chat-feedback", sentimentFilter],
    queryFn: () =>
      api.listChatFeedback({
        sentiment: sentimentFilter === "all" ? null : sentimentFilter,
      }),
    refetchInterval: 30_000,
  });

  if (q.isLoading) return <Loading />;
  if (q.error)
    return (
      <ErrorPanel
        message={(q.error as Error).message ?? t("app.feedback_loading")}
      />
    );
  if (!q.data) return null;

  const items = q.data.items;
  const totalUp = q.data.total_up;
  const totalDown = q.data.total_down;
  const total = totalUp + totalDown;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("app.feedback_title")}
        subtitle={t("app.feedback_subtitle")}
        breadcrumbs={[
          { label: t("app.feedback_breadcrumb_ai"), href: "/ai" },
          { label: t("app.feedback_breadcrumb_self") },
        ]}
      />

      {/* Summary tiles + filter chips */}
      <div className="flex flex-wrap items-center gap-3">
        <SentimentTile
          icon={ThumbsUp}
          countLabel={t("app.feedback_summary_up", { count: totalUp })}
          tone="emerald"
          active={sentimentFilter === "up"}
          onClick={() => setSentimentFilter("up")}
        />
        <SentimentTile
          icon={ThumbsDown}
          countLabel={t("app.feedback_summary_down", { count: totalDown })}
          tone="rose"
          active={sentimentFilter === "down"}
          onClick={() => setSentimentFilter("down")}
        />
        <button
          type="button"
          onClick={() => setSentimentFilter("all")}
          className={`rounded-md border px-2.5 py-1 text-xs ${
            sentimentFilter === "all"
              ? "border-brand bg-brand text-brand-fg"
              : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
          }`}
        >
          {t("app.feedback_summary_total", { count: total })}
        </button>
      </div>

      {items.length === 0 ? (
        <EmptyState
          headline={t("app.feedback_empty")}
          description=""
          icon={Sparkles}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border-subtle bg-surface-default">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-xs uppercase tracking-wider text-text-secondary">
              <tr>
                <th className="px-3 py-2 text-start">
                  {t("app.feedback_col_sentiment")}
                </th>
                <th className="px-3 py-2 text-start">
                  {t("app.feedback_col_message")}
                </th>
                <th className="px-3 py-2 text-start">
                  {t("app.feedback_col_comment")}
                </th>
                <th className="px-3 py-2 text-start">
                  {t("app.feedback_col_user")}
                </th>
                <th className="px-3 py-2 text-start">
                  {t("app.feedback_col_when")}
                </th>
                <th className="px-3 py-2 text-end" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {items.map((row) => (
                <FeedbackRow
                  key={row.id}
                  row={row}
                  isMine={me !== null && me === row.clerk_user_id}
                  onCleared={() =>
                    qc.invalidateQueries({ queryKey: ["chat-feedback"] })
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SentimentTile({
  icon: Icon,
  countLabel,
  tone,
  active,
  onClick,
}: {
  icon: typeof ThumbsUp;
  countLabel: string;
  tone: "emerald" | "rose";
  active: boolean;
  onClick: () => void;
}) {
  const tones = {
    emerald: active
      ? "border-emerald-300 bg-emerald-100 text-emerald-900"
      : "border-emerald-200 bg-emerald-50 text-emerald-900 hover:bg-emerald-100",
    rose: active
      ? "border-rose-300 bg-rose-100 text-rose-900"
      : "border-rose-200 bg-rose-50 text-rose-900 hover:bg-rose-100",
  } as const;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium ${tones[tone]}`}
    >
      <Icon size={12} aria-hidden />
      {countLabel}
    </button>
  );
}

function FeedbackRow({
  row,
  isMine,
  onCleared,
}: {
  row: ChatFeedback;
  isMine: boolean;
  onCleared: () => void;
}) {
  const { t } = useTranslation();
  const clearMut = useMutation({
    mutationFn: () => api.deleteChatFeedback(row.id),
    onSuccess: onCleared,
  });

  return (
    <tr className="align-top">
      <td className="px-3 py-2.5">
        {row.sentiment === "up" ? (
          <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700">
            <ThumbsUp size={11} aria-hidden />
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded bg-rose-50 px-1.5 py-0.5 text-xs font-medium text-rose-700">
            <ThumbsDown size={11} aria-hidden />
          </span>
        )}
      </td>
      <td className="max-w-[420px] px-3 py-2.5 text-xs text-text-secondary">
        {row.message_snippet ?? "—"}
      </td>
      <td className="max-w-[300px] px-3 py-2.5 text-xs italic text-text-secondary">
        {row.comment || "—"}
      </td>
      <td className="px-3 py-2.5 text-xs text-text-tertiary">
        <span className="inline-flex items-center gap-1">
          <User size={11} aria-hidden />
          <span className="font-mono">
            {row.clerk_user_id.slice(0, 12)}…
          </span>
        </span>
      </td>
      <td className="whitespace-nowrap px-3 py-2.5 text-xs text-text-tertiary">
        {row.created_at.slice(0, 16).replace("T", " ")}
      </td>
      <td className="px-3 py-2.5 text-end">
        {isMine && (
          <button
            type="button"
            onClick={() => clearMut.mutate()}
            disabled={clearMut.isPending}
            aria-label={t("common.delete")}
            title={t("common.delete")}
            className="rounded p-1 text-text-tertiary hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50"
          >
            <Trash2 size={12} aria-hidden />
          </button>
        )}
      </td>
    </tr>
  );
}
